import type { ConsoleConfig, Dataset, GateState } from "../../api/types";
import { PURPOSES } from "../../api/types";
import {
  formatDate,
  formatMargin,
  formatScore,
  pluralize,
} from "../../lib/format";
import { metricLabel, PURPOSE_LABELS } from "../../lib/metrics";

/** What each gate state is called on screen. It is never "failed". */
const GATE_LABELS: Record<GateState, string> = {
  no_bar: "no bar",
  bar_set: "bar set",
  bar_cleared: "bar cleared",
  not_yet_passed: "not yet passed",
};

/** The tone each gate state carries, so colour is never the only signal. */
const GATE_TONES: Record<GateState, string> = {
  no_bar: "neutral",
  bar_set: "pending",
  bar_cleared: "ok",
  not_yet_passed: "warning",
};

interface DealGateProps {
  dataset: Dataset;
  /** The published configuration, for the metric's own label. */
  config: ConsoleConfig | undefined;
}

/**
 * The top of every dataset page, and the reason this product gets opened.
 *
 * Baseline and threshold are different numbers and both are shown: the
 * baseline is what the client's own model scores, the threshold is what we
 * agreed to beat, and clients routinely set the bar above their own baseline.
 * A baseline we do not have is empty **with its reason** — an invented
 * baseline is the worst number in the building.
 *
 * Args:
 *     dataset: The dataset, with the gate the server derived on this read.
 *     config: The published configuration, for the metric's label.
 *
 * Returns:
 *     The deal-gate header box.
 */
export function DealGate({ dataset, config }: DealGateProps): JSX.Element {
  const { gate, consent } = dataset;
  const label = metricLabel(config, dataset.task_type, gate.metric);

  return (
    <section
      className="gate-box"
      data-tone={GATE_TONES[gate.state]}
      aria-label="Where this deal stands"
    >
      <div className="gate-box__head">
        <span className="gate-box__who">
          {dataset.account.name} · {dataset.slug}
        </span>
        <span className="gate-box__state" data-tone={GATE_TONES[gate.state]}>
          {GATE_LABELS[gate.state]}
        </span>
      </div>

      {gate.state === "no_bar" ? (
        <p className="gate-box__line">
          No bar yet. Nobody has told us what this has to beat — that is a
          state, not a zero, and you can record one the moment the client says
          it.
        </p>
      ) : (
        <p className="gate-box__line">
          <strong>
            Beat {formatScore(gate.threshold_value)} {label}
          </strong>
          {gate.baseline_value !== null ? (
            <> · their model scores {formatScore(gate.baseline_value)}</>
          ) : (
            <>
              {" "}
              · no baseline:{" "}
              {gate.baseline_absent_reason ?? "the client has not told us"}
            </>
          )}
        </p>
      )}

      {gate.best ? (
        <p className="gate-box__line">
          Best so far {formatScore(gate.best.value)} — version{" "}
          {gate.best.version_number}, {gate.best.recorded_by},{" "}
          {formatDate(gate.best.recorded_at)} ({formatMargin(gate.best.margin)}).
          Reported, not measured by this console.
        </p>
      ) : gate.state === "bar_set" ? (
        <p className="gate-box__line">
          Nothing has been recorded against it yet.
        </p>
      ) : null}

      {gate.attempts > 0 ? (
        <p className="gate-box__meta">
          {pluralize(gate.attempts, "attempt")} recorded, all of them listed
          below.
        </p>
      ) : null}

      <p className="gate-box__permissions">
        {PURPOSES.map((purpose) => {
          const allowed = consent.permissions.includes(purpose);
          return (
            <span
              key={purpose}
              className="gate-box__permission"
              data-allowed={allowed}
            >
              {PURPOSE_LABELS[purpose]} {allowed ? "✓" : "—"}
            </span>
          );
        })}
      </p>

      <p className="gate-box__meta">
        Consent recorded {formatDate(consent.at)} by {consent.recorded_by} —{" "}
        {consent.evidence}
      </p>
    </section>
  );
}
